import * as React from 'react'
import { type ReactNode, useRef, useState } from 'react'

import { cn } from '~/utils/misc.tsx'

export type InputVariant =
  | 'outlined'
  | 'outlined-label'
  | 'filled'
  | 'bottom-line'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: React.ReactNode
  variant?: InputVariant
}

export interface InputLabelPlaceholderProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  rightIcon?: ReactNode
  leftIcon?: ReactNode
}

const InputLabelPlaceholder = React.forwardRef<
  HTMLInputElement,
  InputLabelPlaceholderProps
>(
  (
    { className, type, placeholder, id, value, rightIcon, leftIcon, ...props },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [isFocused, setIsFocused] = useState(false)

    const labelShowPlaceholderStyles = `
        peer-placeholder-shown:top-2.5 peer-placeholder-shown:translate-y-0  peer-placeholder-shown:scale-100
        peer-placeholder-shown:text-foreground-muted peer-placeholder-shown:font-normal     `
    const labelShowLabelStyles = `
        left-5 top-0 -translate-y-2 scale-75 duration-200 origin-top-left text-foreground-muted font-semibold peer-focus:font-semibold
        peer-focus:left-5 peer-focus:top-0 peer-focus:-translate-y-2 peer-focus:scale-75 peer-focus:text-foreground
`

    const handleFocus = (event: any) => {
      props.onFocus && props.onFocus(event)
      setIsFocused(true)
    }
    const handleBlur = (event: any) => {
      props.onBlur && props.onBlur(event)
      setIsFocused(false)
    }
    return (
      <fieldset
        className={cn(
          `relative h-[44px] w-full cursor-text rounded-full border border-input px-4 hover:border-foreground-muted focus:border-2 ${
            isFocused && 'border-2 border-foreground-muted'
          }`,
          props.readOnly && '!cursor-auto !border !border-input',
          className,
        )}
      >
        <input
          ref={el => {
            // Assign the input ref to the provided ref
            if (ref) {
              if (typeof ref === 'function') {
                ref(el)
              } else if (ref.hasOwnProperty('current')) {
                // @ts-ignore
                ref.current = el
              }
            }
            inputRef.current = el
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type={type}
          id={id ?? placeholder}
          aria-describedby="filled_success_help"
          className={cn(
            inputStandardClassName,
            `peer -ml-4 block h-full w-[calc(100%+32px)] appearance-none !rounded-full
          border-none
           bg-white py-3
           pl-5 text-sm   text-foreground focus:ring-0
        `,
            leftIcon && 'pl-10 pt-[14px]',
          )}
          placeholder=" "
          name={`input-${placeholder}`}
          value={value}
          {...props}
        />
        <legend
          className={`invisible h-[5px] px-1 text-xs  peer-placeholder-shown:hidden peer-focus:block`}
        >
          {placeholder}
        </legend>
        {leftIcon && (
          <div
            className="absolute left-4 top-0 flex h-full cursor-text items-center text-foreground-muted"
            onClick={() => inputRef.current!.focus()}
          >
            {leftIcon}
          </div>
        )}
        <label
          htmlFor={id ?? placeholder}
          className={cn(
            'absolute cursor-text font-normal',
            labelShowLabelStyles,
            labelShowPlaceholderStyles,
            leftIcon && 'peer-placeholder-shown:pl-5 peer-focus:pl-0',
            props.readOnly && '!text-foreground-muted',
          )}
        >
          {placeholder}
        </label>
        {rightIcon && <div className="absolute right-4 top-4">{rightIcon}</div>}
      </fieldset>
    )
  },
)

InputLabelPlaceholder.displayName = 'InputLabelPlaceholder'

export const inputStandardClassName = `flex h-11 w-full rounded-full border border-input px-3 py-2 text-sm outline-none
   file:border-0 file:bg-transparent file:text-sm file:font-medium
   placeholder:text-foreground-not-selected focus-visible:outline-none focus-visible:border-2
   focus-visible:border-foreground hover:border-foreground disabled:cursor-not-allowed
   disabled:opacity-50 aria-[invalid]:border-input-invalid`

const inputBottomLineClassName = `flex h-11 w-full border-b-2 border-HPS px-3 text-sm outline-none bg-none placeholder:text-foreground-muted focus-visible:outline-none  focus-visible:border-3 hover:border-foreground-muted disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid`

export interface InputStandardProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: React.ReactNode
  variant: InputVariant
}

const InputStandard = React.forwardRef<HTMLInputElement, InputStandardProps>(
  ({ className, type, leftIcon, variant, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const inputClassName =
      variant === 'filled'
        ? `${inputStandardClassName} bg-gray-100`
        : variant === 'bottom-line'
          ? inputBottomLineClassName
          : inputStandardClassName
    return leftIcon ? (
      <div className={cn('relative w-full', className)}>
        <div
          className="absolute left-4 flex h-11 cursor-text items-center text-foreground-muted"
          onClick={() => inputRef.current!.focus()}
        >
          {leftIcon}
        </div>
        <input
          type={type}
          className={cn(
            inputClassName,
            leftIcon ? 'pl-10' : 'pl-8 ',
            className,
          )}
          ref={el => {
            // Assign the input ref to the provided ref
            if (ref) {
              if (typeof ref === 'function') {
                ref(el)
              } else if (ref.hasOwnProperty('current')) {
                // @ts-ignore
                ref.current = el
              }
            }
            inputRef.current = el
          }}
          {...props}
        />
      </div>
    ) : (
      <input
        type={type}
        className={cn(inputClassName, className)}
        ref={ref}
        {...props}
      />
    )
  },
)

InputStandard.displayName = 'InputStandard'

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ variant = 'outlined', ...props }, ref) => {
    return variant === 'outlined' ? (
      <InputStandard variant={variant} {...props} ref={ref} />
    ) : variant === 'outlined-label' ? (
      <InputLabelPlaceholder {...props} ref={ref} />
    ) : variant === 'filled' ? (
      <InputStandard variant={variant} {...props} ref={ref} />
    ) : variant === 'bottom-line' ? (
      <InputStandard variant={variant} {...props} ref={ref} />
    ) : null
  },
)

Input.displayName = 'Input'

export { Input }
